import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  if (s < 86400) return Math.floor(s / 3600) + "h ago";
  if (s < 7 * 86400) return Math.floor(s / 86400) + "d ago";
  return d.toLocaleDateString();
};

const FeedItem = ({ row, onLikeToggled }) => {
  const { user } = useAuth();
  const [liked, setLiked] = useState(!!row.liked_by_me);
  const [count, setCount] = useState(row.like_count || 0);
  const [busy, setBusy] = useState(false);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error("log in to like"); return; }
    if (busy) return;
    setBusy(true);
    const prevL = liked, prevC = count;
    setLiked(!prevL); setCount(prevL ? prevC - 1 : prevC + 1);
    try {
      const { data } = await api.post("/comments/" + row.id + "/like");
      setLiked(data.liked); setCount(data.count);
      if (onLikeToggled) onLikeToggled();
    } catch (err) {
      setLiked(prevL); setCount(prevC);
      toast.error("could not like");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-coal border border-white/10 p-4 hover:border-hotpink/40 transition-colors" data-testid={"feed-item-" + row.id}>
      <div className="flex items-baseline gap-3 mb-2 flex-wrap">
        <span className="scribble text-hotpink text-lg">{row.user_name || "anon"}</span>
        <span className="text-white/40 text-xs font-outfit">{timeAgo(row.created_at)}</span>
        <Link to={"/episodes/" + row.episode_number} className="text-white/50 text-[10px] uppercase tracking-widest font-outfit hover:text-hotpink ml-auto">
          on Ep {String(row.episode_number).padStart(2, "0")} · {row.episode_title}
        </Link>
      </div>
      <p className="font-outfit text-white/90 text-sm md:text-base whitespace-pre-wrap line-clamp-3 mb-2">{row.body}</p>
      <div className="flex items-center gap-4 text-[10px] uppercase tracking-widest font-outfit text-white/50">
        <button onClick={toggle} disabled={busy} data-testid={"feed-like-" + row.id} className={"flex items-center gap-1 transition-colors " + (liked ? "text-hotpink" : "hover:text-hotpink")}>
          <Heart size={12} fill={liked ? "currentColor" : "none"} /> {count > 0 ? count : "like"}
        </button>
        <Link to={"/episodes/" + row.episode_number} className="hover:text-hotpink flex items-center gap-1">
          <MessageCircle size={12} /> reply on thread
        </Link>
      </div>
    </div>
  );
};

export default function LatestComments({ limit = 8, title = "THE THREAD.", subtitle = "what everyone's saying", compact = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/comments/recent", { params: { limit } });
      setRows(data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [limit]);

  if (loading) return null;
  if (!rows.length && compact) return null;

  return (
    <div data-testid="latest-comments-feed">
      {!compact && (
        <div className="mb-5">
          <p className="scribble text-hotpink text-xl rotate-[-2deg] inline-block mb-1">{subtitle}</p>
          <h2 className="brush-title text-3xl md:text-5xl text-white">{title.split(" ").map((w, i) => i === 1 ? <span key={i} className="text-hotpink">{w} </span> : <span key={i}>{w} </span>)}</h2>
        </div>
      )}
      {compact && (
        <h3 className="font-outfit font-bold text-white text-sm uppercase tracking-widest mb-3 flex items-center gap-2">
          <MessageCircle size={14} className="text-hotpink"/> Latest comments
        </h3>
      )}
      {rows.length === 0 ? (
        <p className="text-white/40 font-outfit text-sm italic">no comments yet — start the chaos.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {rows.map((r) => <FeedItem key={r.id} row={r} onLikeToggled={load} />)}
        </div>
      )}
    </div>
  );
}
